import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Menu, X, ExternalLink, BarChart3 } from "lucide-react";
import { DISCORD_LINK, LOGO_URL, SOCIAL_LINKS } from "@/constants";

// Custom SVG icons for social media
const YoutubeIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TwitterIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const PatreonIcon = ({ className }) => (
  <svg className={className || "w-5 h-5"} viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.386.524c-4.764 0-8.64 3.876-8.64 8.64 0 4.75 3.876 8.613 8.64 8.613 4.75 0 8.614-3.864 8.614-8.613C24 4.4 20.136.524 15.386.524M.003 23.537h4.22V.524H.003"/>
  </svg>
);

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isReportPage = location.pathname.startsWith("/report");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Home", href: "/#home" },
    { label: "Our Approach", href: "/#approach" },
    { label: "Research & Screens", href: "/#research" },
    { label: "Membership", href: "/#membership" },
    { 
      label: "Reports", 
      items: [
        { label: "Nasdaq", href: "/report/nasdaq" },
        { label: "PSX", href: "/report/psx" },
        { label: "Tasi", href: "/report/tasi" }
      ]
    },
    { label: "FAQ", href: "/#faq" },
    { label: "Contact", href: "/#contact" },
  ];

  const socialLinks = [
    { icon: YoutubeIcon, href: SOCIAL_LINKS.youtube, label: "YouTube" },
    { icon: PatreonIcon, href: SOCIAL_LINKS.patreon, label: "Patreon" },
    { icon: FacebookIcon, href: SOCIAL_LINKS.facebook, label: "Facebook" },
    { icon: TwitterIcon, href: SOCIAL_LINKS.twitter, label: "Twitter/X" },
    { icon: InstagramIcon, href: SOCIAL_LINKS.instagram, label: "Instagram" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-ics-navy/95 backdrop-blur-lg border-b border-white/5 py-2"
          : "bg-ics-navy py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <a href="/#home" data-testid="nav-logo" className="flex items-center">
            <img
              src={LOGO_URL}
              alt="Investment Circle Shariah"
              className="h-14 md:h-16 lg:h-20 w-auto"
            />
          </a>

          <div className="hidden lg:flex items-center gap-6">
            {navItems.map((item) =>
              item.items ? (
                <div key={item.label} className="relative group">
                  <button
                    className={`nav-link text-sm font-medium flex items-center gap-1 transition-colors ${
                      isReportPage ? "text-ics-orange" : "text-ics-gray hover:text-ics-white"
                    }`}
                  >
                    {item.label}
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  <div className="absolute left-0 mt-2 w-32 rounded-md shadow-lg bg-ics-navy border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="py-1">
                      {item.items.map((subItem) => (
                        <Link
                          key={subItem.label}
                          to={subItem.href}
                          className={`block px-4 py-2 text-sm transition-colors ${location.pathname === subItem.href ? "text-ics-orange" : "text-ics-gray hover:text-white hover:bg-white/5"}`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : item.href.startsWith("/") && !item.href.includes("#") ? (
                <Link
                  key={item.label}
                  to={item.href}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`nav-link text-sm font-medium transition-colors ${
                    isReportPage && item.href.startsWith("/report")
                      ? "text-ics-orange"
                      : "text-ics-gray hover:text-ics-white"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="nav-link text-sm font-medium text-ics-gray hover:text-ics-white transition-colors"
                >
                  {item.label}
                </a>
              )
            )}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href="/#research"
              data-testid="nav-sample-research-btn"
              className="text-sm font-medium text-ics-gray hover:text-ics-white transition-colors"
            >
              View Sample Research
            </a>
            <a
              href={DISCORD_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-join-discord-btn"
              className="cta-primary inline-flex items-center gap-2 bg-ics-orange hover:bg-ics-orange-hover text-white font-semibold px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105"
            >
              Join the Discord
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-ics-white bg-ics-navy rounded-lg"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4 bg-ics-navy">
            <div className="flex flex-col gap-4">
              {navItems.map((item) =>
                item.items ? (
                  <div key={item.label} className="flex flex-col gap-2">
                    <div className={`text-sm font-medium ${isReportPage ? "text-ics-orange" : "text-ics-gray"}`}>
                      {item.label}
                    </div>
                    <div className="flex flex-col pl-4 border-l border-white/10 gap-3">
                      {item.items.map((subItem) => (
                        <Link
                          key={subItem.label}
                          to={subItem.href}
                          className={`transition-colors text-sm ${
                            location.pathname === subItem.href ? "text-ics-orange" : "text-ics-gray hover:text-ics-white"
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : item.href.startsWith("/") && !item.href.includes("#") ? (
                  <Link
                    key={item.label}
                    to={item.href}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`transition-colors ${
                      isReportPage && item.href.startsWith("/report")
                        ? "text-ics-orange"
                        : "text-ics-gray hover:text-ics-white"
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.label}
                    href={item.href}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-ics-gray hover:text-ics-white transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                )
              )}
              
              {/* Social Media Icons in Mobile Menu */}
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-white/10">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-social-${social.label.toLowerCase().replace('/', '-')}`}
                    className="w-10 h-10 rounded-full bg-white/5 hover:bg-ics-orange/20 flex items-center justify-center transition-colors"
                    title={social.label}
                  >
                    <span className="text-ics-gray hover:text-ics-orange">
                      <social.icon />
                    </span>
                  </a>
                ))}
              </div>

              <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-white/10">
                <a
                  href="/#research"
                  data-testid="mobile-sample-research-btn"
                  className="text-sm font-medium text-ics-gray hover:text-ics-white"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  View Sample Research
                </a>
                <a
                  href={DISCORD_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-join-discord-btn"
                  className="inline-flex items-center justify-center gap-2 bg-ics-orange hover:bg-ics-orange-hover text-white font-semibold px-5 py-3 rounded-full transition-all"
                >
                  Join the Discord
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
